import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import { SurveyRequest, SurveyResponse } from '../types';
import './SurveyModal.css';

interface SurveyModalProps {
  isOpen: boolean;
  sessionId: string;
  origin: 'demo-end' | 'feedback-button';
  demoEndAnswers?: { wantToContinue: boolean; wantToGiveFeedback: boolean };
  onClose: () => void;
}

const SurveyModal: React.FC<SurveyModalProps> = ({
  isOpen,
  sessionId,
  origin,
  demoEndAnswers,
  onClose
}) => {
  // Email section state
  const [wantToContinue, setWantToContinue] = useState<boolean | null>(null);
  const [email, setEmail] = useState('');
  const [confirmedAdult, setConfirmedAdult] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  // Survey questions state
  const [howFun, setHowFun] = useState<string | null>(null);
  const [understandHowToPlay, setUnderstandHowToPlay] = useState<string | null>(null);
  const [noticedIssues, setNoticedIssues] = useState<string | null>(null);
  const [issueDescription, setIssueDescription] = useState('');
  const [selectedNeeds, setSelectedNeeds] = useState<string[]>([]);
  const [somethingElseText, setSomethingElseText] = useState('');

  // UI state
  const [showThankYou, setShowThankYou] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setWantToContinue(null);
      setEmail('');
      setConfirmedAdult(false);
      setEmailError(null);
      setHowFun(null);
      setUnderstandHowToPlay(null);
      setNoticedIssues(null);
      setIssueDescription('');
      setSelectedNeeds([]);
      setSomethingElseText('');
      setShowThankYou(false);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  // Determine which parts to show
  const showEmailSection =
    (origin === 'demo-end' && demoEndAnswers?.wantToContinue) ||
    (origin === 'feedback-button');

  const showEmailInput =
    (origin === 'demo-end' && demoEndAnswers?.wantToContinue) ||
    (origin === 'feedback-button' && wantToContinue === true);

  const showSurveyPart =
    (origin === 'demo-end' && demoEndAnswers?.wantToGiveFeedback) ||
    (origin === 'feedback-button');

  const showWantToContinueQuestion = origin === 'feedback-button';

  // Email validation
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle multi-select for "what needs work"
  const toggleNeedSelection = (need: string) => {
    if (selectedNeeds.includes(need)) {
      setSelectedNeeds(selectedNeeds.filter(n => n !== need));
    } else {
      setSelectedNeeds([...selectedNeeds, need]);
    }
  };

  const handleSubmit = async () => {
    // Validate email if provided
    if (email) {
      if (!validateEmail(email)) {
        setEmailError('Please enter a valid email address');
        return;
      }
      if (!confirmedAdult) {
        setEmailError('Please confirm you are over 18 to provide your email');
        return;
      }
    }

    setEmailError(null);
    setIsSubmitting(true);

    try {
      const requestData: SurveyRequest = {
        session_id: sessionId,
        origin,
        email: email || undefined,
        email_confirmed_adult: confirmedAdult || undefined,
        want_to_continue: wantToContinue ?? undefined,
        how_fun: howFun ?? undefined,
        understand_how_to_play: understandHowToPlay ?? undefined,
        noticed_issues: noticedIssues ? {
          has_issues: noticedIssues === 'yes',
          description: issueDescription || undefined
        } : undefined,
        what_needs_work: selectedNeeds.length > 0 ? selectedNeeds.map(need => {
          if (need === 'Something else' && somethingElseText) {
            return `Something else: ${somethingElseText}`;
          }
          return need;
        }) : undefined
      };

      await axios.post<SurveyResponse>(`${API_URL}/feedback/survey/`, requestData);

      setShowThankYou(true);
    } catch (error) {
      console.error('Error submitting survey:', error);
      alert('There was an error submitting your feedback. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setShowThankYou(false);
    setIsSubmitting(false);
    onClose();
  };

  const thankYouMessage = origin === 'feedback-button'
    ? "Thank you! Now back to your adventure with Percy and Alfie..."
    : `Thank you for playing and for your feedback. It made our day!\n\nA game is just a bunch of random code on a computer, until someone plays it. You added the magic.\n\nMore adventures with Percy and Alfie coming soon. Tell your friends (if you liked the game. If you hated it, tell your enemies!)`;

  if (showThankYou) {
    return (
      <div className="survey-overlay">
        <div className="survey-modal">
          <div className="survey-thank-you">
            <h2>🎉 Thank You!</h2>
            <p className="thank-you-message">{thankYouMessage}</p>
            <button className="close-button" onClick={handleClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="survey-overlay">
      <div className="survey-modal">
        <button className="modal-close-x" onClick={handleClose} aria-label="Close">×</button>

        <div className="survey-header">
          <h2>📝 We'd Love Your Feedback!</h2>
        </div>

        <div className="survey-content">
          {/* Part 1: Email Collection */}
          {showEmailSection && (
            <div className="survey-section email-section">
              <h3>Stay Updated</h3>

              {showWantToContinueQuestion && (
                <div className="question-block">
                  <p className="question">Would you like to play more when we fix some bugs and add more adventure?</p>
                  <div className="radio-group horizontal">
                    <label className={`radio-option ${wantToContinue === true ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        checked={wantToContinue === true}
                        onChange={() => setWantToContinue(true)}
                      />
                      <span>Yes</span>
                    </label>
                    <label className={`radio-option ${wantToContinue === false ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        checked={wantToContinue === false}
                        onChange={() => setWantToContinue(false)}
                      />
                      <span>No</span>
                    </label>
                  </div>
                </div>
              )}

              {showEmailInput && (
                <div className="question-block">
                  <p className="question">Parent/Guardian: please provide YOUR email to be notified of major updates:</p>
                  <input
                    type="email"
                    className="email-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your.email@example.com"
                  />

                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={confirmedAdult}
                      onChange={(e) => setConfirmedAdult(e.target.checked)}
                    />
                    <span>I confirm I am over 18</span>
                  </label>

                  {emailError && <div className="error-message">{emailError}</div>}
                </div>
              )}
            </div>
          )}

          {/* Part 2: Survey Questions */}
          {showSurveyPart && (
            <div className="survey-section questions-section">
              <h3>Your Experience</h3>

              {/* How fun was the game? */}
              <div className="question-block">
                <p className="question">How fun was the game?</p>
                <div className="emoji-options">
                  <label className={`emoji-option ${howFun === 'super-fun' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="how_fun"
                      checked={howFun === 'super-fun'}
                      onChange={() => setHowFun('super-fun')}
                    />
                    <div className="emoji-content">
                      <span className="emoji">😍</span>
                      <span className="label">Super fun</span>
                    </div>
                  </label>
                  <label className={`emoji-option ${howFun === 'fun' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="how_fun"
                      checked={howFun === 'fun'}
                      onChange={() => setHowFun('fun')}
                    />
                    <div className="emoji-content">
                      <span className="emoji">😊</span>
                      <span className="label">Fun</span>
                    </div>
                  </label>
                  <label className={`emoji-option ${howFun === 'ok' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="how_fun"
                      checked={howFun === 'ok'}
                      onChange={() => setHowFun('ok')}
                    />
                    <div className="emoji-content">
                      <span className="emoji">😐</span>
                      <span className="label">OK<br/>[but I won't play again]</span>
                    </div>
                  </label>
                  <label className={`emoji-option ${howFun === 'not-really' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="how_fun"
                      checked={howFun === 'not-really'}
                      onChange={() => setHowFun('not-really')}
                    />
                    <div className="emoji-content">
                      <span className="emoji">😕</span>
                      <span className="label">Not really<br/>[I want my time back!]</span>
                    </div>
                  </label>
                </div>
              </div>

              {/* Did you understand how to play? */}
              <div className="question-block">
                <p className="question">Did you understand how to play and what to do?</p>
                <div className="radio-group vertical">
                  <label className={`radio-option ${understandHowToPlay === 'yup' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="understand"
                      checked={understandHowToPlay === 'yup'}
                      onChange={() => setUnderstandHowToPlay('yup')}
                    />
                    <span>Yup</span>
                  </label>
                  <label className={`radio-option ${understandHowToPlay === 'mostly' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="understand"
                      checked={understandHowToPlay === 'mostly'}
                      onChange={() => setUnderstandHowToPlay('mostly')}
                    />
                    <span>Mostly, but some parts were confusing</span>
                  </label>
                  <label className={`radio-option ${understandHowToPlay === 'nope' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="understand"
                      checked={understandHowToPlay === 'nope'}
                      onChange={() => setUnderstandHowToPlay('nope')}
                    />
                    <span>Nope, I was pretty lost</span>
                  </label>
                </div>
              </div>

              {/* Did you notice anything weird? */}
              <div className="question-block">
                <p className="question">Did you notice anything weird, confusing, or inappropriate?</p>
                <div className="radio-group horizontal">
                  <label className={`radio-option ${noticedIssues === 'yes' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="noticed_issues"
                      checked={noticedIssues === 'yes'}
                      onChange={() => setNoticedIssues('yes')}
                    />
                    <span>Yes</span>
                  </label>
                  <label className={`radio-option ${noticedIssues === 'no' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="noticed_issues"
                      checked={noticedIssues === 'no'}
                      onChange={() => setNoticedIssues('no')}
                    />
                    <span>No</span>
                  </label>
                </div>
                {noticedIssues === 'yes' && (
                  <textarea
                    className="issue-description"
                    value={issueDescription}
                    onChange={(e) => setIssueDescription(e.target.value)}
                    placeholder="Please tell us what..."
                    rows={3}
                  />
                )}
              </div>

              {/* What needs the most work? */}
              <div className="question-block">
                <p className="question">What needs the most work? (select all that apply)</p>
                <div className="checkbox-group">
                  {[
                    'The game was confusing',
                    'The game was boring',
                    'The illustrations are not cute',
                    'I was stuck in a puzzle',
                    "It's too buggy/broken",
                    'Story generation takes too long',
                    "Actually nothing. It's pretty good!",
                    'Something else'
                  ].map(need => (
                    <label key={need} className={`checkbox-option ${selectedNeeds.includes(need) ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={selectedNeeds.includes(need)}
                        onChange={() => toggleNeedSelection(need)}
                      />
                      <span>{need}</span>
                    </label>
                  ))}
                </div>
                {selectedNeeds.includes('Something else') && (
                  <input
                    type="text"
                    className="something-else-input"
                    value={somethingElseText}
                    onChange={(e) => setSomethingElseText(e.target.value)}
                    placeholder="Please tell us what..."
                  />
                )}
              </div>
            </div>
          )}
        </div>

        <div className="survey-actions">
          <button className="submit-button" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SurveyModal;
